/**
 * Client-side Nova trigger evaluation.
 * Runs when user is on Dashboard to evaluate milestone rules and deliver/queue notifications.
 */
import { useEffect, useRef } from 'react';
import { runForUser } from '../lib/triggerRunner';

/**
 * Runs trigger evaluation for the current user. Call once when Dashboard mounts.
 * Delivery: writes to fired_triggers, creates in-app Nova notification.
 * Push: stubbed until push infrastructure exists.
 */
export function useNovaTriggerCheck({
  user,
  timeline,
  logs,
  firedTriggers,
  lossHistory,
  addFiredTrigger,
  addNovaNotification,
}) {
  const hasRun = useRef(false);

  useEffect(() => {
    if (!user || hasRun.current) return;
    const key = String(user?.email || user?.id || '').toLowerCase();
    if (!key) return;

    hasRun.current = true;

    const userTimezone =
      typeof Intl !== 'undefined' && Intl.DateTimeFormat?.().resolvedOptions?.().timeZone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'UTC';

    runForUser({
      user,
      timeline: timeline || {},
      logs: logs || [],
      firedTriggers: firedTriggers || [],
      lossHistory: lossHistory || [],
      userTimezone,
      addFiredTrigger: (userId, messageId) => addFiredTrigger?.(messageId),
      onDeliver: (u, messageId, resolvedMessage) => {
        addNovaNotification?.({ message_id: messageId, message: resolvedMessage });
        // TODO: Send push notification when FCM/APNs wired
      },
      onQueue: () => {
        // Queued for cron delivery when user enters 8am-8pm window.
        // Client does not persist queue; cron handles late delivery.
      },
    });
  }, [
    user,
    timeline,
    logs,
    firedTriggers,
    lossHistory,
    addFiredTrigger,
    addNovaNotification,
  ]);
}
